import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from 'lib/supabase';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { ChevronDown, ChevronUp, Star, Plus, Trash2, Save, AlertCircle, CheckCircle, Upload, X } from 'lucide-react';

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  const bg = type === 'success' ?'bg-green-50 border-green-200 text-green-700' :'bg-red-50 border-red-200 text-red-700';
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg text-sm font-medium ${bg}`}>
      {type === 'success'
        ? <CheckCircle size={18} />
        : <AlertCircle size={18} />}
      {message}
    </div>
  );
};

// ─── Accordion Panel ──────────────────────────────────────────────────────────
const AccordionPanel = ({ title, isOpen, onToggle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-gray-50 transition-colors text-left"
    >
      <span className="text-base font-semibold text-gray-900">{title}</span>
      {isOpen ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
    </button>
    {isOpen && (
      <div className="px-6 py-5 bg-white border-t border-gray-100">
        {children}
      </div>
    )}
  </div>
);

// ─── Star Rating Input ────────────────────────────────────────────────────────
const StarRatingInput = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5]?.map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => onChange(star)}
        className="focus:outline-none"
      >
        <Star
          size={20}
          fill={star <= value ? '#f59e0b' : 'none'}
          color={star <= value ? '#f59e0b' : '#d1d5db'}
        />
      </button>
    ))}
  </div>
);

// ─── SUB-SECTION 1: Testi Hero ────────────────────────────────────────────────
const TestiHeroSection = ({ showToast }) => {
  const [phrases, setPhrases] = useState([]);
  const [intervalMs, setIntervalMs] = useState(2500);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase?.from('landing_config')?.select('key, value')?.in('key', ['hero_phrases', 'hero_interval_ms']);
        if (error) throw error;
        data?.forEach((row) => {
          if (row?.key === 'hero_phrases') {
            try {
              const parsed = JSON.parse(row?.value) || [];
              if (parsed?.length > 0 && typeof parsed?.[0] === 'string') {
                // backward compat: migrate string[] to object[]
                setPhrases(parsed?.map((s) => ({ title: s, subtitle: '' })));
              } else {
                setPhrases(parsed);
              }
            } catch { setPhrases([]); }
          }
          if (row?.key === 'hero_interval_ms') {
            setIntervalMs(parseInt(row?.value, 10) || 2500);
          }
        });
      } catch (err) {
        showToast(err?.message || 'Errore nel caricamento', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePhraseChange = (index, field, value) => {
    setPhrases((prev) => prev?.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleAddPhrase = () => setPhrases((prev) => [...(prev || []), { title: '', subtitle: '' }]);

  const handleDeletePhrase = (index) => {
    setPhrases((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const phrasesJson = JSON.stringify(phrases?.filter((p) => p?.title?.trim() !== ''));
      const { error: e1 } = await supabase?.from('landing_config')?.upsert({ key: 'hero_phrases', value: phrasesJson, updated_at: new Date()?.toISOString() }, { onConflict: 'key' });
      if (e1) throw e1;
      const { error: e2 } = await supabase?.from('landing_config')?.upsert({ key: 'hero_interval_ms', value: String(intervalMs), updated_at: new Date()?.toISOString() }, { onConflict: 'key' });
      if (e2) throw e2;
      showToast('Testi hero salvati');
    } catch (err) {
      showToast(err?.message || 'Errore nel salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-500 py-4">Caricamento...</div>;

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Frasi rotanti</label>
        <div className="space-y-2">
          {phrases?.map((phrase, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={phrase?.title}
                onChange={(e) => handlePhraseChange(index, 'title', e?.target?.value)}
                placeholder="Titolo (es. Casa pulita, semplice.)"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                value={phrase?.subtitle}
                onChange={(e) => handlePhraseChange(index, 'subtitle', e?.target?.value)}
                placeholder="Sottotitolo (es. Pulizie dalla tua zona, in 24 ore.)"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => handleDeletePhrase(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Elimina frase"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={handleAddPhrase}
          className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          <Plus size={16} /> Aggiungi frase
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Durata per frase (ms)
        </label>
        <p className="text-xs text-gray-500 mb-2">Valore attuale: {intervalMs} ms</p>
        <input
          type="number"
          value={intervalMs}
          onChange={(e) => setIntervalMs(parseInt(e?.target?.value, 10) || 2500)}
          min={500}
          max={10000}
          step={100}
          className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
      >
        <Save size={16} />
        {saving ? 'Salvataggio...' : 'Salva testi hero'}
      </button>
    </div>
  );
};

// ─── SUB-SECTION 2: Recensioni ────────────────────────────────────────────────
const ReviewCard = ({ review, onSave, onDelete, onFeaturedChange }) => {
  const [form, setForm] = useState({ ...review });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleFeaturedToggle = (checked) => {
    handleChange('is_featured', checked);
    if (checked) onFeaturedChange(review?.id);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminare questa recensione?')) return;
    setDeleting(true);
    await onDelete(form?.id);
    setDeleting(false);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nome autore</label>
          <input
            type="text"
            value={form?.author_name || ''}
            onChange={(e) => handleChange('author_name', e?.target?.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ruolo</label>
          <input
            type="text"
            value={form?.author_role || ''}
            onChange={(e) => handleChange('author_role', e?.target?.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Città</label>
          <input
            type="text"
            value={form?.location || ''}
            onChange={(e) => handleChange('location', e?.target?.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Stelle</label>
          <StarRatingInput value={form?.rating || 5} onChange={(v) => handleChange('rating', v)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ordine</label>
          <input
            type="number"
            value={form?.sort_order ?? 0}
            onChange={(e) => handleChange('sort_order', parseInt(e?.target?.value, 10) || 0)}
            className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form?.is_featured || false}
              onChange={(e) => handleFeaturedToggle(e?.target?.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            In evidenza
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form?.is_active !== false}
              onChange={(e) => handleChange('is_active', e?.target?.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Attiva
          </label>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Testo recensione</label>
        <textarea
          value={form?.quote_text || ''}
          onChange={(e) => handleChange('quote_text', e?.target?.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
        />
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          <Save size={14} />
          {saving ? 'Salvataggio...' : 'Salva'}
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-100 disabled:opacity-60 transition-colors border border-red-200"
        >
          <Trash2 size={14} />
          {deleting ? 'Eliminazione...' : 'Elimina'}
        </button>
      </div>
    </div>
  );
};

const RecensioniSection = ({ showToast }) => {
  const [reviews, setReviews] = useState([]);
  const [reviewsIntervalMs, setReviewsIntervalMs] = useState(4000);
  const [loading, setLoading] = useState(true);
  const [savingInterval, setSavingInterval] = useState(false);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const reviewsResult = await supabase?.from('landing_reviews')?.select('*')?.order('sort_order', { ascending: true });
      const configResult = await supabase?.from('landing_config')?.select('key, value')?.eq('key', 'reviews_interval_ms');
      const reviewsData = reviewsResult?.data;
      const reviewsError = reviewsResult?.error;
      const configData = configResult?.data;
      const configError = configResult?.error;
      if (reviewsError) throw reviewsError;
      if (configError) throw configError;
      setReviews(reviewsData || []);
      const intervalRow = configData?.find((r) => r?.key === 'reviews_interval_ms');
      if (intervalRow) setReviewsIntervalMs(parseInt(intervalRow?.value, 10) || 4000);
    } catch (err) {
      showToast(err?.message || 'Errore nel caricamento', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleSaveInterval = async () => {
    setSavingInterval(true);
    try {
      const { error } = await supabase?.from('landing_config')?.upsert({ key: 'reviews_interval_ms', value: String(reviewsIntervalMs), updated_at: new Date()?.toISOString() }, { onConflict: 'key' });
      if (error) throw error;
      showToast('Impostazioni recensioni salvate');
    } catch (err) {
      showToast(err?.message || 'Errore nel salvataggio', 'error');
    } finally {
      setSavingInterval(false);
    }
  };

  const handleSave = async (form) => {
    try {
      if (form?.id && !form?.id?.startsWith('new-')) {
        const { error } = await supabase?.from('landing_reviews')?.update({
            author_name: form?.author_name,
            author_role: form?.author_role,
            location: form?.location,
            rating: form?.rating,
            quote_text: form?.quote_text,
            is_featured: form?.is_featured,
            sort_order: form?.sort_order,
            is_active: form?.is_active,
          })?.eq('id', form?.id);
        if (error) throw error;
      } else {
        const { error } = await supabase?.from('landing_reviews')?.insert({
            author_name: form?.author_name || '',
            author_role: form?.author_role || 'Cliente verificato',
            location: form?.location || '',
            rating: form?.rating || 5,
            quote_text: form?.quote_text || '',
            is_featured: form?.is_featured || false,
            sort_order: form?.sort_order || 0,
            is_active: form?.is_active !== false,
          });
        if (error) throw error;
      }
      showToast('Recensione salvata');
      await fetchReviews();
    } catch (err) {
      showToast(err?.message || 'Errore nel salvataggio', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      if (id?.startsWith?.('new-')) {
        setReviews((prev) => prev?.filter((r) => r?.id !== id));
        return;
      }
      const { error } = await supabase?.from('landing_reviews')?.delete()?.eq('id', id);
      if (error) throw error;
      showToast('Recensione eliminata');
      await fetchReviews();
    } catch (err) {
      showToast(err?.message || 'Errore nell\'eliminazione', 'error');
    }
  };

  const handleFeaturedChange = async (featuredId) => {
    // Unfeature all others in UI immediately
    setReviews((prev) =>
      prev?.map((r) => ({ ...r, is_featured: r?.id === featuredId }))
    );
    // Persist: unfeature all, then feature the selected one
    try {
      await supabase?.from('landing_reviews')?.update({ is_featured: false })?.neq('id', featuredId);
      await supabase?.from('landing_reviews')?.update({ is_featured: true })?.eq('id', featuredId);
    } catch (err) {
      showToast(err?.message || 'Errore aggiornamento evidenza', 'error');
    }
  };

  const handleAddNew = () => {
    const newReview = {
      id: `new-${Date.now()}`,
      author_name: '',
      author_role: 'Cliente verificato',
      location: '',
      rating: 5,
      quote_text: '',
      is_featured: false,
      sort_order: reviews?.length || 0,
      is_active: true,
    };
    setReviews((prev) => [...(prev || []), newReview]);
  };

  if (loading) return <div className="text-sm text-gray-500 py-4">Caricamento...</div>;

  return (
    <div className="space-y-4">
      {/* Interval setting — first field in this sub-section */}
      <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Durata per recensione (ms)
          </label>
          <p className="text-xs text-gray-500 mb-2">Ogni quanti ms cambia la recensione in evidenza</p>
          <input
            type="number"
            value={reviewsIntervalMs}
            onChange={(e) => setReviewsIntervalMs(parseInt(e?.target?.value, 10) || 4000)}
            min={1000}
            max={30000}
            step={500}
            className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
        <button
          onClick={handleSaveInterval}
          disabled={savingInterval}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          <Save size={16} />
          {savingInterval ? 'Salvataggio...' : 'Salva impostazioni'}
        </button>
      </div>

      {/* Review cards */}
      {reviews?.map((review) => (
        <ReviewCard
          key={review?.id}
          review={review}
          onSave={handleSave}
          onDelete={handleDelete}
          onFeaturedChange={handleFeaturedChange}
        />
      ))}
      {reviews?.length === 0 && (
        <p className="text-sm text-gray-500 py-2">Nessuna recensione. Aggiungine una.</p>
      )}
      <button
        onClick={handleAddNew}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium mt-2"
      >
        <Plus size={16} /> Aggiungi recensione
      </button>
    </div>
  );
};

// ─── SUB-SECTION 3: Video ─────────────────────────────────────────────────────
const VideoCard = ({ video, onSave, onDelete, onDefaultChange, showToast }) => {
  const [form, setForm] = useState({ ...video });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleDefaultToggle = (checked) => {
    handleChange('is_default', checked);
    if (checked) onDefaultChange(video?.id);
  };

  const handleVideoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const timestamp = Date.now();
      const safeName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `videos/${timestamp}_${safeName}`;
      const { error: uploadError } = await supabase?.storage?.from('landing-media')?.upload(path, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase?.storage?.from('landing-media')?.getPublicUrl(path);
      const publicUrl = urlData?.publicUrl;
      setForm((prev) => ({ ...prev, external_url: publicUrl }));
      showToast('Video caricato con successo');
    } catch (err) {
      showToast(err?.message || 'Errore nel caricamento video', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminare questo video?')) return;
    setDeleting(true);
    await onDelete(form?.id);
    setDeleting(false);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Titolo</label>
          <input
            type="text"
            value={form?.title || ''}
            onChange={(e) => handleChange('title', e?.target?.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Posizione</label>
          <p className="text-xs text-gray-400 mb-1">(1 = mostrato per primo)</p>
          <input
            type="number"
            value={form?.sort_order ?? 0}
            onChange={(e) => handleChange('sort_order', parseInt(e?.target?.value, 10) || 0)}
            className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
      </div>
      {/* File upload */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">Carica video</label>
        <div className="flex items-center gap-3">
          <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${uploading ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            <Upload size={15} />
            {uploading ? 'Caricamento...' : 'Scegli file'}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={handleVideoUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
          {uploading && <span className="text-xs text-blue-600 font-medium animate-pulse">Caricamento in corso...</span>}
        </div>
      </div>
      {/* URL field */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">o incolla URL</label>
        <input
          type="text"
          value={form?.external_url || ''}
          onChange={(e) => handleChange('external_url', e?.target?.value)}
          placeholder="https://..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        />
      </div>
      {/* Video preview */}
      {form?.external_url?.trim() && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Anteprima</label>
          <video
            src={form?.external_url}
            controls
            style={{ maxHeight: '120px', borderRadius: '8px' }}
            className="w-auto border border-gray-200"
          />
        </div>
      )}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={form?.is_default || false}
            onChange={(e) => handleDefaultToggle(e?.target?.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Video di default
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={form?.is_active !== false}
            onChange={(e) => handleChange('is_active', e?.target?.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Attivo
        </label>
      </div>
      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          <Save size={14} />
          {saving ? 'Salvataggio...' : 'Salva'}
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-100 disabled:opacity-60 transition-colors border border-red-200"
        >
          <Trash2 size={14} />
          {deleting ? 'Eliminazione...' : 'Elimina'}
        </button>
      </div>
    </div>
  );
};

const VideoSection = ({ showToast }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('landing_media')?.select('*')?.eq('type', 'reviews_video')?.order('sort_order', { ascending: true });
      if (error) throw error;
      setVideos(data || []);
    } catch (err) {
      showToast(err?.message || 'Errore nel caricamento', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  // Always UPDATE by id — every card in the list has a real DB id
  const handleSave = async (form) => {
    try {
      const { error } = await supabase?.from('landing_media')?.update({
          title: form?.title,
          external_url: form?.external_url,
          is_default: form?.is_default,
          sort_order: form?.sort_order,
          is_active: form?.is_active,
        })?.eq('id', form?.id);
      if (error) throw error;
      showToast('Video salvato');
      await fetchVideos();
    } catch (err) {
      showToast(err?.message || 'Errore nel salvataggio', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      // Check if the deleted video was the default
      const deletedVideo = videos?.find((v) => v?.id === id);
      const wasDefault = deletedVideo?.is_default;

      const { error } = await supabase?.from('landing_media')?.delete()?.eq('id', id);
      if (error) throw error;

      // If it was the default, promote the next available active video
      if (wasDefault) {
        const remaining = videos?.filter((v) => v?.id !== id && v?.is_active !== false);
        if (remaining?.length > 0) {
          await supabase?.from('landing_media')?.update({ is_default: true })?.eq('id', remaining?.[0]?.id);
        }
      }

      showToast('Video eliminato');
      await fetchVideos();
    } catch (err) {
      showToast(err?.message || 'Errore nell\'eliminazione', 'error');
    }
  };

  // Enforce single default: uncheck all others, check the selected one
  const handleDefaultChange = async (defaultId) => {
    // Optimistic UI update
    setVideos((prev) =>
      prev?.map((v) => ({ ...v, is_default: v?.id === defaultId }))
    );
    try {
      await supabase?.from('landing_media')?.update({ is_default: false })?.eq('type', 'reviews_video')?.neq('id', defaultId);
      await supabase?.from('landing_media')?.update({ is_default: true })?.eq('id', defaultId);
    } catch (err) {
      showToast(err?.message || 'Errore aggiornamento default', 'error');
      await fetchVideos(); // revert on error
    }
  };

  // INSERT a new row immediately — never add a local-only entry
  const handleAddNew = async () => {
    setAdding(true);
    try {
      const { data, error } = await supabase?.from('landing_media')?.insert({
          type: 'reviews_video',
          title: '',
          external_url: '',
          is_default: false,
          sort_order: videos?.length || 0,
          is_active: true,
        })?.select()?.single();
      if (error) throw error;
      showToast('Nuovo video aggiunto');
      await fetchVideos();
    } catch (err) {
      showToast(err?.message || 'Errore nell\'aggiunta video', 'error');
    } finally {
      setAdding(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-500 py-4">Caricamento...</div>;

  return (
    <div className="space-y-4">
      {videos?.map((video) => (
        <VideoCard
          key={video?.id}
          video={video}
          onSave={handleSave}
          onDelete={handleDelete}
          onDefaultChange={handleDefaultChange}
          showToast={showToast}
        />
      ))}
      {videos?.length === 0 && (
        <p className="text-sm text-gray-500 py-2">Nessun video. Aggiungine uno.</p>
      )}
      <button
        onClick={handleAddNew}
        disabled={adding}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium mt-2 disabled:opacity-60"
      >
        <Plus size={16} /> {adding ? 'Aggiunta in corso...' : 'Aggiungi video'}
      </button>
    </div>
  );
};

// ─── SUB-SECTION 4: Immagine Hero ─────────────────────────────────────────────
const ImmagineHeroSection = ({ showToast }) => {
  const [activeUrl, setActiveUrl] = useState('');
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [imgSizePct, setImgSizePct] = useState(100);
  const [imgHeight, setImgHeight] = useState('16:9');
  const [imgObjectFit, setImgObjectFit] = useState('cover');
  const [savingDimensions, setSavingDimensions] = useState(false);
  const fileInputRef = useRef(null);

  // ── helpers ──────────────────────────────────────────────────────────────
  const upsertConfig = async (key, value) => {
    const { error } = await supabase?.from('landing_config')?.upsert(
      { key, value, updated_at: new Date()?.toISOString() },
      { onConflict: 'key' }
    );
    if (error) throw error;
  };

  const saveActiveUrl = async (url) => {
    await upsertConfig('hero_image_url', url);
  };

  const saveLibrary = async (arr) => {
    await upsertConfig('hero_image_library', JSON.stringify(arr));
  };

  // ── fetch on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          ?.from('landing_config')
          ?.select('key, value')
          ?.in('key', ['hero_image_url', 'hero_image_library', 'hero_image_size_pct', 'hero_image_height', 'hero_image_object_fit']);
        if (error) throw error;
        data?.forEach((row) => {
          if (row?.key === 'hero_image_url') setActiveUrl(row?.value || '');
          if (row?.key === 'hero_image_library') {
            try { setLibrary(JSON.parse(row?.value) || []); } catch { setLibrary([]); }
          }
          if (row?.key === 'hero_image_size_pct') setImgSizePct(Number(row?.value) || 100);
          if (row?.key === 'hero_image_height') setImgHeight(row?.value || '16:9');
          if (row?.key === 'hero_image_object_fit') setImgObjectFit(row?.value || 'cover');
        });
      } catch (err) {
        showToast(err?.message || 'Errore nel caricamento', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ── set active (click thumbnail) ──────────────────────────────────────────
  const handleSetActive = async (url) => {
    try {
      await saveActiveUrl(url);
      setActiveUrl(url);
      showToast('Immagine hero aggiornata');
    } catch (err) {
      showToast(err?.message || 'Errore nel salvataggio', 'error');
    }
  };

  // ── remove from library ───────────────────────────────────────────────────
  const handleRemove = async (url) => {
    try {
      const newLibrary = library?.filter((u) => u !== url);
      await saveLibrary(newLibrary);
      setLibrary(newLibrary);
      if (activeUrl === url) {
        await saveActiveUrl('');
        setActiveUrl('');
      }
      showToast('Immagine rimossa dalla libreria');
    } catch (err) {
      showToast(err?.message || 'Errore nella rimozione', 'error');
    }
  };

  // ── upload file ───────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const timestamp = Date.now();
      const safeName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `hero/${timestamp}_${safeName}`;
      const { error: uploadError } = await supabase?.storage
        ?.from('landing-media')
        ?.upload(path, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase?.storage?.from('landing-media')?.getPublicUrl(path);
      const publicUrl = urlData?.publicUrl;
      const newLibrary = [...(library || []), publicUrl];
      await saveLibrary(newLibrary);
      await saveActiveUrl(publicUrl);
      setLibrary(newLibrary);
      setActiveUrl(publicUrl);
      showToast('Immagine caricata e impostata come attiva');
    } catch (err) {
      showToast(err?.message || 'Errore nel caricamento', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  // ── add URL ───────────────────────────────────────────────────────────────
  const handleAddUrl = async () => {
    const trimmed = urlInput?.trim();
    if (!trimmed) return;
    try {
      const newLibrary = [...(library || []), trimmed];
      await saveLibrary(newLibrary);
      await saveActiveUrl(trimmed);
      setLibrary(newLibrary);
      setActiveUrl(trimmed);
      setUrlInput('');
      showToast('URL aggiunto e impostato come attivo');
    } catch (err) {
      showToast(err?.message || 'Errore nel salvataggio', 'error');
    }
  };

  // ── save dimensions ───────────────────────────────────────────────────────
  const handleSaveDimensions = async () => {
    setSavingDimensions(true);
    try {
      const { error: e1 } = await supabase?.from('landing_config')?.upsert(
        { key: 'hero_image_size_pct', value: String(imgSizePct), updated_at: new Date()?.toISOString() },
        { onConflict: 'key' }
      );
      if (e1) throw e1;
      const { error: e2 } = await supabase?.from('landing_config')?.upsert(
        { key: 'hero_image_height', value: imgHeight, updated_at: new Date()?.toISOString() },
        { onConflict: 'key' }
      );
      if (e2) throw e2;
      const { error: e3 } = await supabase?.from('landing_config')?.upsert(
        { key: 'hero_image_object_fit', value: imgObjectFit, updated_at: new Date()?.toISOString() },
        { onConflict: 'key' }
      );
      if (e3) throw e3;
      showToast('Dimensioni immagine salvate');
    } catch (err) {
      showToast(err?.message || 'Errore nel salvataggio', 'error');
    } finally {
      setSavingDimensions(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-500 py-4">Caricamento...</div>;

  return (
    <div className="space-y-6">
      {/* ── Thumbnail library row ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Libreria immagini
          {library?.length > 0 && (
            <span className="ml-2 text-xs text-gray-400 font-normal">
              ({library?.length} {library?.length === 1 ? 'immagine' : 'immagini'})
            </span>
          )}
        </label>

        {library?.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-2">
            Nessuna immagine nella libreria. Carica o incolla un URL qui sotto.
          </p>
        ) : (
          <div
            className="flex gap-3 overflow-x-auto pb-2"
            style={{ scrollbarWidth: 'thin' }}
          >
            {library?.map((url, idx) => {
              const isActive = url === activeUrl;
              return (
                <div
                  key={idx}
                  className="relative flex-shrink-0 cursor-pointer"
                  style={{
                    width: 120,
                    height: 80,
                    borderRadius: 8,
                    border: isActive ? '2px solid #2563eb' : '2px solid transparent',
                    boxSizing: 'border-box',
                  }}
                  onClick={() => handleSetActive(url)}
                  title={isActive ? 'Immagine attiva' : 'Clicca per impostare come attiva'}
                >
                  <img
                    src={url}
                    alt={`Immagine libreria ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 6,
                      display: 'block',
                    }}
                    onError={(e) => {
                      e.target.style.background = '#f3f4f6';
                      e.target.style.display = 'none';
                      e.target.parentElement.style.background = '#f3f4f6';
                    }}
                  />
                  {/* Active checkmark badge */}
                  {isActive && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        left: 4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#2563eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <path d="M2 5.5L4.5 8L9 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                  {/* Remove button */}
                  <button
                    onClick={(e) => { e?.stopPropagation(); handleRemove(url); }}
                    title="Rimuovi immagine"
                    style={{
                      position: 'absolute',
                      top: 3,
                      right: 3,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.55)',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                  >
                    <X size={10} color="white" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Image size controls ── */}
      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-4">
        <p className="text-sm font-semibold text-gray-800">Dimensioni immagine</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Dimensione immagine (%)</label>
            <input
              type="number"
              min={10}
              max={100}
              step={5}
              value={imgSizePct}
              onChange={(e) => setImgSizePct(Number(e?.target?.value))}
              placeholder="es. 80"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
            <p className="text-xs text-gray-400 mt-1">100 = larghezza piena, 80 = 80% del contenitore</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Aspect ratio</label>
            <select
              value={imgHeight}
              onChange={(e) => setImgHeight(e?.target?.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="16:9">16:9 (widescreen)</option>
              <option value="4:3">4:3 (classic)</option>
              <option value="1:1">1:1 (square)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fit</label>
            <select
              value={imgObjectFit}
              onChange={(e) => setImgObjectFit(e?.target?.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="cover">Cover (crop to fit)</option>
              <option value="contain">Contain (whole image visible)</option>
            </select>
          </div>
        </div>
        <button
          onClick={handleSaveDimensions}
          disabled={savingDimensions}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          <Save size={16} />
          {savingDimensions ? 'Salvataggio...' : 'Salva dimensioni'}
        </button>
      </div>

      {/* ── Upload button ── */}
      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-2">
        <p className="text-sm font-semibold text-gray-800">Carica immagine</p>
        <label
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium cursor-pointer transition-colors w-full justify-center ${
            uploading
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' :'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Upload size={15} />
          {uploading ? 'Caricamento...' : 'Carica immagine'}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
        {uploading && (
          <p className="text-xs text-blue-600 font-medium animate-pulse text-center">
            Caricamento in corso...
          </p>
        )}
      </div>
      {/* ── Paste URL block ── */}
      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
        <p className="text-sm font-semibold text-gray-800">Incolla URL</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e?.target?.value)}
            onKeyDown={(e) => { if (e?.key === 'Enter') handleAddUrl(); }}
            placeholder="https://..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
          <button
            onClick={handleAddUrl}
            disabled={!urlInput?.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            <Plus size={14} />
            Aggiungi URL
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const AdminLandingPageManagement = () => {
  const [openPanel, setOpenPanel] = useState('hero');
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const togglePanel = (key) => setOpenPanel((prev) => (prev === key ? null : key));

  const panels = [
    { key: 'hero', title: 'Testi Hero', component: <TestiHeroSection showToast={showToast} /> },
    { key: 'recensioni', title: 'Recensioni', component: <RecensioniSection showToast={showToast} /> },
    { key: 'video', title: 'Video', component: <VideoSection showToast={showToast} /> },
    { key: 'immagine', title: 'Immagine Hero', component: <ImmagineHeroSection showToast={showToast} /> },
  ];

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Landing Page</h1>
          <p className="text-gray-600 mt-2">Gestisci i contenuti della homepage</p>
        </div>

        <div className="space-y-3">
          {panels?.map((panel) => (
            <AccordionPanel
              key={panel?.key}
              title={panel?.title}
              isOpen={openPanel === panel?.key}
              onToggle={() => togglePanel(panel?.key)}
            >
              {panel?.component}
            </AccordionPanel>
          ))}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminDashboardLayout>
  );
};

export default AdminLandingPageManagement;
